import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';

import { useProfile } from '@boss/auth';
import { PAYMENT_TYPE } from '@boss/services/client';
import {
  AddressBlock,
  Alert,
  Button,
  PricingCard,
  ProductOverviewCard,
  ShoppingCartTotalPrice,
  Skeleton,
} from '@boss/ui';
import { formatPrice } from '@boss/utils';

import { useAccount, useCreatePaymentRequest, useGetBasket, useGetEventById } from '../../client-queries';
import { useStoreById } from '../../client-queries/store/queries';
import { EventOverviewCard } from '../../components';
import { ORDER_ID_PARAMETER } from '../../constants/checkout';
import { useWorkshopAddress } from '../../hooks';
import { buildAddressString } from '../checkoutPage/orderOverview';

const WorkshopCheckoutPage = () => {
  const { t } = useTranslation('checkout');
  const { locale, query } = useRouter();

  const basketId = query[ORDER_ID_PARAMETER] as string;
  const paymentFailed = query.paymentFailed === 'true';

  const { isLoggedIn, accountId } = useProfile();
  const { data: account, isLoading: accountLoading } = useAccount(locale as string);
  const { data: basket, isLoading: basketLoading } = useGetBasket(basketId ?? 0, !!basketId);
  // Guest only address from localstorage
  const { address: workshopAddress } = useWorkshopAddress(basketId);

  // Extract workshop code from basket
  const workshopCode = useMemo(() => {
    return basket?.lines?.find(line => line.workshop)?.workshop?.code;
  }, [basket]);

  const { data: event, isLoading: eventLoading } = useGetEventById(workshopCode ?? '');

  const { data: store, isLoading: storeLoading } = useStoreById(event?.locationId, !!event?.locationId);

  const { mutateAsync: createPaymentRequest } = useCreatePaymentRequest();

  const invoiceAddress =
    !isLoggedIn && workshopAddress ? workshopAddress : account?.addresses.find(adr => adr.type === 'invoice');

  const firstName = !isLoggedIn ? workshopAddress?.firstname : account?.accountinfo?.firstname;
  const lastName = !isLoggedIn ? workshopAddress?.lastname : account?.accountinfo?.lastname;

  const isAccountLoading = isLoggedIn && accountId !== '' ? accountLoading : false;
  const isLoading = isAccountLoading || basketLoading || eventLoading || storeLoading;

  const handlePaymentRequest = async () => {
    if (!event || !store?.paymentproviderserviceid) {
      console.error('Missing event or store service ID for payment request');
      return;
    }

    const paymentInfo = await createPaymentRequest({
      type: PAYMENT_TYPE.cashWorkshop,
      locale: locale as string,
      referenceId: basketId,
      amount: basket?.pricesummary.amounttopay ?? 0,
      accountId: basket?.account.accountnumber,
      description: `Workshop - ${event?.title}`,
      storeServiceId: store.paymentproviderserviceid,
    });

    if (paymentInfo?.paymentUrl) {
      window.location.href = paymentInfo?.paymentUrl;
    }
  };

  return (
    <div className="my-3">
      <div className="flex flex-col gap-4">
        {paymentFailed && <Alert type="error">{t('errors.payment.title', { ns: 'basket' })}</Alert>}
        <h2>{t('overviewPageTitle')}</h2>
        <div className="grid gap-3 lg:grid-cols-6">
          <ProductOverviewCard className="lg:col-span-4">
            <h3 className="text-workshopCheckout-order-title-text h4">{t('orderOverview.title')}</h3>

            <div className="flex gap-5">
              {isAccountLoading && (
                <div className="my-5 flex flex-col gap-2">
                  <h5 className="text-workshopCheckout-invoice-address-text font-bold">
                    {t('orderOverview.invoiceAddress')}
                  </h5>
                  <Skeleton className="w-50 h-40" />
                </div>
              )}
              {invoiceAddress && (
                <AddressBlock title={t('orderOverview.invoiceAddress') ?? ''}>
                  <div className="flex flex-col">
                    {firstName && lastName && (
                      <h5 className="pb-5 font-bold">
                        {firstName} {lastName}
                      </h5>
                    )}
                    {buildAddressString(invoiceAddress as Record<string, string>, 'invoice')}
                  </div>
                </AddressBlock>
              )}
            </div>
            <h3 className="text-workshopCheckout-details-title-text h4 mb-4">{t('workshop.workshopDetailsTitle')}</h3>
            {eventLoading || !event ? <Skeleton className="h-40 w-full" /> : <EventOverviewCard event={event} />}
          </ProductOverviewCard>
          <div className="flex flex-col gap-3 lg:col-span-2">
            <PricingCard title="">
              <ShoppingCartTotalPrice
                className="mb-10"
                infoText={t('inclCosts', { ns: 'basket' })}
                isPriceLoading={basketLoading}
                price={formatPrice(basket?.pricesummary.amounttopay ?? 0, locale as string)}
                totalText={t('totalWithVAT', { ns: 'basket' })}
              />
            </PricingCard>
            <Button
              className="w-full py-3 text-center"
              disabled={isLoading}
              icon={faArrowRight}
              iconPosition="right"
              label={t('summary.payAndContinue', { ns: 'checkout' })}
              onClick={handlePaymentRequest}
              type="primary"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkshopCheckoutPage;
